/**
 * Les couleurs des DONNÉES : celles qu'on choisit pour un projet ou une habitude, et qui sont
 * enregistrées avec eux. Ce ne sont pas des jetons du thème — le thème ne sait rien d'un
 * projet — et c'est le seul fichier du front où une couleur s'écrit à la main
 * (`tests/design.test.ts`). Elles atteignent l'écran par `--teinte` (`lib/teinte.ts`).
 *
 * Braise 2 (2026-10-01) : huit tons de même luminance, posés sur le noir chaud du thème. Les
 * anciennes couleurs saturées n'avaient pas la même clarté — un projet bordeaux disparaissait,
 * une habitude turquoise criait — et faisaient un arc-en-ciel plus fort que la marque. Les
 * couleurs déjà enregistrées ne sont pas réécrites en base : `affichee()` les traduit à l'écran.
 */

/** Fallback when a habit has no colour. */
export const DEFAULT_HABIT_COLOR = '#7FA8E6';

/** Shared swatches of the project / habit colour pickers; each picker appends its own last one. */
export const BASE_PALETTE: readonly string[] = [
  '#8FC28F', // sauge
  DEFAULT_HABIT_COLOR, // ciel
  '#E9B44C', // ambre
  '#E483A4', // rose
  '#A693E6', // iris
  '#5FB8B3', // lagon
  '#F08A5D', // braise
];

export const PROJECT_PALETTE: readonly string[] = [...BASE_PALETTE, '#CBB497'];
export const HABIT_PALETTE: readonly string[] = [...BASE_PALETTE, '#CDA8F0'];

/** Les couleurs d'avant Braise 2, telles qu'enregistrées, vers leur ton d'aujourd'hui. */
const ANCIENNES: Record<string, string> = {
  '#22A05B': '#8FC28F',
  '#4488DD': DEFAULT_HABIT_COLOR,
  '#D4940A': '#E9B44C',
  '#9B2848': '#E483A4',
  '#6D4CAB': '#A693E6',
  '#0F9EA5': '#5FB8B3',
  '#C45A11': '#F08A5D',
  '#8A8F98': '#CBB497',
  '#A78BFA': '#CDA8F0',
};

/** La couleur qu'on MONTRE pour une couleur enregistrée : une ancienne prend son ton Braise 2. */
export function affichee(couleur: string): string;
export function affichee(couleur: string | null | undefined): string | null | undefined;
export function affichee(couleur: string | null | undefined): string | null | undefined {
  if (!couleur) return couleur;
  return ANCIENNES[couleur.toUpperCase()] ?? couleur;
}

/** Ce qu'un lecteur d'écran dit d'une pastille : un nom, pas un code. */
export const COLOR_NAMES: Record<string, string> = {
  '#8FC28F': 'Sage',
  [DEFAULT_HABIT_COLOR]: 'Sky',
  '#E9B44C': 'Amber',
  '#E483A4': 'Rose',
  '#A693E6': 'Iris',
  '#5FB8B3': 'Lagoon',
  '#F08A5D': 'Ember',
  '#CBB497': 'Sand',
  '#CDA8F0': 'Lilac',
};
